import { useEffect, useMemo, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { notificar } from '@/lib/notificar';
import { haJanelaAberta } from '@/lib/useFocoDeModal';
import { MAPA_FIXO, type IdComando } from './mapaFixo';

/**
 * Mapa central de atalhos de teclado da tela de venda (feature 013, T014).
 *
 * **Um lugar só, de propósito** (skill de projeto `react-hotkeys-pdv`): um
 * `keydown` solto dentro de um componente não é auditável — ninguém consegue
 * responder "quais teclas o PDV escuta?" sem varrer a base, e a próxima feature
 * que registrar uma tecla colide com esta em silêncio. Todo atalho global novo
 * entra por aqui.
 *
 * ### Por que `react-hotkeys-hook`, e não um `keydown` próprio
 *
 * Decisão do usuário (2026-09-05), que revogou a primeira implementação desta
 * feature: o PDV vai reservar **outras** teclas de função — F1, F2, F3 — para
 * ações futuras, e a partir daí o problema deixa de ser "escutar quatro teclas"
 * e passa a ser arbitragem entre vários donos. A biblioteca já resolve o que um
 * ouvinte artesanal reimplementaria errado aos poucos: registro por escopo,
 * combinações com modificador, sequências, ativação condicional e — o mais
 * importante aqui — a lista de tags de formulário em que um atalho **não** deve
 * disparar. Ver AD-176 em `.specs/project/STATE.md`.
 *
 * ### O invariante que este módulo protege (`FR-014`, `SC-005`)
 *
 * Os atalhos ficam ativos **durante toda a venda**, então a não colisão com
 * digitação e com a bipagem do leitor de código de barras é requisito de
 * correção, não higiene. O leitor se comporta como um teclado muito rápido
 * digitando dentro do campo de produto: a defesa correta é ignorar o evento
 * enquanto o foco estiver num campo de entrada, e **não** medir intervalo entre
 * teclas, que confundiria um operador rápido com um leitor.
 *
 * A exceção é o próprio campo de código do produto, que se declara transparente
 * aos atalhos — ver `ATRIBUTO_ATALHOS_PERMITIDOS` abaixo.
 */

/** O que um atalho global precisa declarar. */
export interface AtalhoDeTeclado {
  /** Nome da tecla como o navegador a reporta (`'F6'`), sem modificadores. */
  readonly tecla: string;
  readonly aoAcionar: () => void;
}

/**
 * Marca um campo de entrada como **transparente** aos atalhos globais: com o
 * foco nele, a tecla dispara em vez de ser engolida pela digitação.
 *
 * Espalhe no elemento: `<input {...ATRIBUTO_ATALHOS_PERMITIDOS} />`.
 *
 * Existe para o campo de código do produto (decisão do usuário, 2026-09-05).
 * É onde o operador de caixa passa a venda inteira — sai dali para bipar,
 * volta, bipa de novo —, e obrigá-lo a tirar o foco antes de fechar a venda
 * transformaria um toque em três gestos. É a **única** exceção à regra de
 * `FR-014`, e ela é segura pelo mesmo motivo que a regra existe: o leitor de
 * código de barras emite dígitos e `Enter`, nunca teclas de função.
 *
 * A exceção é declarada **no campo**, e não numa lista de seletores aqui
 * dentro: quem lê `EntradaRapidaProduto` vê que aquele input abre mão do
 * bloqueio, e um campo novo não herda a exceção por acidente de seletor.
 */
export const ATRIBUTO_ATALHOS_PERMITIDOS = { 'data-atalhos-globais': 'permitidos' } as const;

/** Etiquetas cujo foco pertence a quem digita, não ao atalho. */
const ETIQUETAS_DE_ENTRADA = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * Papéis ARIA que se comportam como campo ou como item de lista aberta.
 *
 * Espelha a lista que a biblioteca usa em `enableOnFormTags` — precisa ser
 * reproduzida aqui porque este módulo desliga aquela guarda (`enableOnFormTags:
 * true`) para poder abrir a exceção acima. Sem isto, um F6 pressionado com o
 * combobox de condição **aberto** lançaria o pagamento em vez de escolher a
 * opção sob o cursor.
 */
const PAPEIS_DE_ENTRADA = new Set([
  'searchbox',
  'slider',
  'spinbutton',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'radio',
  'textbox',
]);

/** O foco está num lugar onde a tecla pertence a quem digita. */
function ehCampoQueEngoleATecla(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) {
    return false;
  }

  const ehEntrada =
    ETIQUETAS_DE_ENTRADA.has(alvo.tagName) ||
    alvo.isContentEditable ||
    PAPEIS_DE_ENTRADA.has(alvo.getAttribute('role') ?? '');

  return ehEntrada && alvo.dataset['atalhosGlobais'] !== 'permitidos';
}

/**
 * O foco está dentro de uma janela modal.
 *
 * Atalho de venda rápida não dispara por cima de um modal aberto: a janela do
 * vale devolução, a do PIX e as confirmações destrutivas descrevem uma decisão
 * pendente, e lançar um pagamento por baixo delas produziria uma venda que o
 * operador não consegue explicar. O marcador é o mesmo que `useFocoDeModal`
 * assume em toda a base (`role="dialog"`).
 *
 * Não cabe em `enableOnFormTags`: o botão "Confirmar" de um diálogo não é um
 * campo de formulário, e é justamente sobre ele que o foco costuma estar.
 */
function dentroDeModal(alvo: EventTarget | null): boolean {
  return alvo instanceof HTMLElement && alvo.closest('[role="dialog"]') !== null;
}

/**
 * Por que **nenhuma** tecla age agora, ou `null` — a trava da tela inteira
 * (AD-280, revisão de 2026-10-16).
 *
 * Uma só, para os dois mapas: a tela de venda inerte (`inert`) não recebe
 * clique nem foco, mas as teclas são ouvidas no `document` e o `inert` não as
 * alcança. Antes a mesma checagem era repetida por superfície — F1–F4 no
 * `AppShell`, F6–F9 em `useAcionarCenario` —, e a próxima tecla ou o próximo
 * caminho de cenário teria de lembrar de se embrulhar nela. Aqui ela vale para
 * toda tecla registrada por este módulo.
 *
 * Estado de módulo, como a pilha de `haJanelaAberta`: quem trava é um
 * componente (`useTravaDasTeclas`), quem lê é o handler da tecla, no instante da
 * pressionada.
 */
let motivoDaTrava: string | null = null;

/**
 * Trava todas as teclas deste módulo enquanto `motivo` não for `null`. A tecla
 * continua engolida (a posse não muda) e o operador lê o motivo.
 *
 * Um dono só por vez — hoje, `TravaDaVendaDuranteEnvio`. Desmontar o dono
 * destrava.
 */
export function useTravaDasTeclas(motivo: string | null): void {
  useEffect(() => {
    if (motivo === null) {
      return;
    }
    motivoDaTrava = motivo;
    return () => {
      motivoDaTrava = null;
    };
  }, [motivo]);
}

/** Travada, avisa o motivo e responde `true`: a tecla não age. */
function recusadaPelaTrava(): boolean {
  if (motivoDaTrava === null) {
    return false;
  }
  notificar.erro(motivoDaTrava);
  return true;
}

/**
 * A pressionada é da tecla, mas **não aciona** o atalho.
 *
 * `repeat`: tecla segurada dispara um acionamento por pressionada, nunca pela
 * repetição automática do sistema — num atalho que lança pagamento, a diferença
 * é entre um lançamento e uma enxurrada deles.
 *
 * Modal aberto: pela pilha de `useFocoDeModal` **ou** pelo foco dentro de um
 * `role="dialog"` — a pilha pega o foco solto no `body` com a janela aberta, o
 * seletor pega diálogos que não passam pelo hook.
 *
 * Campo de entrada: a regra de `FR-014` da 013, com a exceção declarada no
 * próprio campo (`ATRIBUTO_ATALHOS_PERMITIDOS`).
 *
 * **Nenhuma destas devolve a tecla ao navegador** (achado do usuário,
 * 2026-09-15). Elas moravam em `ignoreEventWhen`, que a biblioteca avalia
 * **antes** do `preventDefault` — com um modal aberto, F6 levava o foco à barra
 * de endereços e F7 ligava a navegação por cursor; com o foco na quantidade, o
 * bipe seguinte ia parar fora da tela. Agora são avaliadas dentro do handler,
 * depois de a tecla já ter sido engolida — o mesmo desenho que a feature 016
 * adotou para F1–F4/F10.
 */
function acionamentoSuprimido(evento: KeyboardEvent): boolean {
  return (
    evento.repeat ||
    haJanelaAberta() ||
    dentroDeModal(evento.target) ||
    ehCampoQueEngoleATecla(evento.target)
  );
}

/**
 * Registra atalhos globais enquanto o componente estiver montado.
 *
 * `ativo` desliga o mapa inteiro sem desmontar o componente — é o que permite à
 * dica de atalhos existir na árvore com a lista vazia sem escutar o teclado.
 *
 * Os atalhos ficam numa `ref` atualizada a cada render, e não nas dependências
 * do hook: o call site monta os handlers inline (fecham sobre o estado da
 * venda), então passá-los adiante reinscreveria o ouvinte a cada render — e a
 * janela entre remover e adicionar é exatamente onde uma tecla se perde. O que
 * de fato muda a inscrição é a **lista de teclas**, e ela é uma string.
 */
export function useAtalhosDeTeclado(atalhos: readonly AtalhoDeTeclado[], ativo = true): void {
  const atalhosRef = useRef<readonly AtalhoDeTeclado[]>(atalhos);
  atalhosRef.current = atalhos;

  // Primitiva, não array: uma lista nova a cada render teria identidade nova e
  // faria o hook rebindar sem nada ter mudado.
  const teclas = useMemo(
    () => atalhos.map((atalho) => atalho.tecla.toLowerCase()).join(','),
    [atalhos],
  );

  useHotkeys(
    teclas,
    (evento) => {
      // A tecla já foi engolida pela biblioteca; daqui para baixo só se decide
      // se o atalho age.
      if (acionamentoSuprimido(evento) || recusadaPelaTrava()) {
        return;
      }
      const alvo = atalhosRef.current.find(
        (atalho) => atalho.tecla.toUpperCase() === evento.key.toUpperCase(),
      );
      alvo?.aoAcionar();
    },
    {
      // Sem tecla registrada, o mapa não escuta nada — e um F6 sem cenário volta
      // a ser do navegador. A posse vale para as teclas **cadastradas**; estendê-la
      // às vagas de F6–F9 seria decisão de spec, como foi o mapa fixo da 016.
      enabled: ativo && atalhos.length > 0,
      /**
       * Casa por `event.key` (a tecla **lógica**), não por `event.code` (a
       * posição física), que é o padrão da biblioteca.
       *
       * É a semântica certa para este mapa: o que as features declaram são
       * nomes de tecla — `'F6'` hoje, `'F1'`/`'F2'` quando as ações futuras
       * chegarem —, e não posições num teclado. Nas teclas de função os dois
       * coincidem em hardware real; a diferença aparece em layout alternativo,
       * onde `code` responderia pela posição e trairia quem leu o cadastro.
       *
       * Tem também um efeito prático: o `user-event` não conhece teclas de
       * função no seu mapa padrão e emite `code: 'Unknown'` para elas, então
       * sem isto o atalho seria inverificável em teste de componente — e um
       * atalho que só o E2E alcança é um atalho sem rede de proteção.
       */
      useKey: true,
      /**
       * F6–F9 têm comportamento nativo (F6 move o foco entre painéis do
       * Chrome): sem isto o atalho lançaria o pagamento **e** tiraria o foco da
       * tela de venda.
       */
      preventDefault: true,
      /**
       * A guarda de campo de entrada é **nossa**, em `acionamentoSuprimido`, e
       * não a da biblioteca. A regra de `FR-014` continua valendo para a
       * **ação** — com o foco num campo o atalho não age —, mas ela precisa
       * admitir uma exceção declarada no próprio campo
       * (`ATRIBUTO_ATALHOS_PERMITIDOS`), e o `enableOnFormTags` da biblioteca é
       * tudo-ou-nada — além de, desligado, devolver a tecla ao navegador.
       */
      enableOnFormTags: true,
      enableOnContentEditable: true,
      // Só a deferência a quem já tratou a tecla: um segundo dono é o que não
      // pode existir. As demais guardas suprimem a ação, nunca a posse.
      ignoreEventWhen: (evento) => evento.defaultPrevented,
    },
  );
}

/* ------------------------------------------------------------------ *
 * Teclas fixas do produto (feature 016)
 * ------------------------------------------------------------------ */

/** O que o call site fornece para um comando do mapa fixo (`contracts` §2). */
export interface AcaoFixa {
  /**
   * O comando pode rodar agora? Devolve a frase da recusa, ou `null`.
   *
   * Consultada **na pressionada**, nunca no render: o estado da venda muda entre
   * o render e o gesto, e num atalho isso é ainda mais provável que num clique.
   */
  readonly indisponivel: () => string | null;
  /** Executa. Só é chamada quando `indisponivel()` devolveu `null`. */
  readonly executar: () => void;
}

/**
 * As teclas do mapa fixo, na forma que a biblioteca espera. Constante de módulo:
 * o conjunto não varia, então a inscrição nunca é refeita.
 */
const TECLAS_FIXAS = MAPA_FIXO.map((entrada) => entrada.tecla.toLowerCase()).join(',');

/**
 * Registra o mapa fixo inteiro (`contracts/atalhos-fixos-api.md` §2). Chamado
 * **uma vez** na árvore — em `AppShell` — para cada tecla ter um dono só (I6).
 *
 * ### Por que um segundo registro, e não `useAtalhosDeTeclado` com uma flag
 *
 * Os dois têm regras **opostas** sobre foco (`research.md` D1). F6–F9 cedem a
 * tecla a quem digita; as fixas a tomam justamente ali (`FR-005`) — é o F3 com
 * o foco na quantidade que abria a busca do Chrome e engolia o bipe seguinte.
 * Um hook com dois modos teria duas razões de mudança e deixaria o leitor
 * adivinhando qual política está valendo.
 *
 * ### Três estágios, nesta ordem (`data-model.md` §4)
 *
 * 1. **Posse.** `enabled` é literal `true` e o `preventDefault` é da biblioteca,
 *    aplicado no casamento da tecla e **antes** do callback. Não há parâmetro,
 *    query, cadastro nem plataforma capaz de devolver a tecla ao navegador
 *    (`FR-001`, `FR-002`). A única deferência é a quem já tratou o evento
 *    (`defaultPrevented`): um segundo dono é o que `FR-004` proíbe.
 * 2. **Elegibilidade.** Repetição automática e janela aberta suprimem a
 *    **ação**, em silêncio — a tecla já foi engolida. Os dois ficam dentro do
 *    handler, e não em `ignoreEventWhen`, porque ali eles pulariam também o
 *    `preventDefault`: segurar F1 abriria a ajuda do navegador a partir da
 *    segunda repetição.
 * 3. **Disponibilidade.** Indisponível, o operador lê o motivo (`FR-003`, I5);
 *    disponível, a ação roda pelo mesmo ponto de entrada do clique (`FR-018`).
 *
 * `acoes` é exaustivo sobre `IdComando`: uma tecla nova no mapa não compila
 * até ganhar ação. Fica numa `ref` pelo mesmo motivo de `useAtalhosDeTeclado` —
 * o call site monta as ações inline, e rebindar a cada render abriria a janela
 * em que uma tecla se perde.
 */
export function useTeclasFixas(acoes: Readonly<Record<IdComando, AcaoFixa>>): void {
  const acoesRef = useRef(acoes);
  acoesRef.current = acoes;

  useHotkeys(
    TECLAS_FIXAS,
    (evento) => {
      const entrada = MAPA_FIXO.find(
        (candidata) => candidata.tecla.toUpperCase() === evento.key.toUpperCase(),
      );
      if (entrada === undefined) {
        return;
      }

      // Estágio 2 — a tecla já é nossa; só a ação fica de fora.
      if (evento.repeat || haJanelaAberta()) {
        return;
      }

      // Estágio 3 — a tela travada vem antes da disponibilidade de cada
      // comando: nenhum deles pode rodar, seja qual for o motivo próprio.
      if (recusadaPelaTrava()) {
        return;
      }
      const acao = acoesRef.current[entrada.comando];
      const motivo = acao.indisponivel();
      if (motivo !== null) {
        notificar.erro(motivo);
        return;
      }
      acao.executar();
    },
    {
      enabled: true,
      // Tecla lógica, como no mapa de F6–F9 — e pelo mesmo efeito prático: o
      // `user-event` emite `code: 'Unknown'` para teclas de função, e sem isto a
      // posse só seria verificável no E2E.
      useKey: true,
      preventDefault: true,
      enableOnFormTags: true,
      enableOnContentEditable: true,
      ignoreEventWhen: (evento) => evento.defaultPrevented,
    },
  );
}
